/* eslint-disable @nx/enforce-module-boundaries */
import { Mutators } from '@axo/form/data-access/backend-sync';
import { Product } from '@axo/shared/data-access/types';
import { DataSharingStatus, MarketCountryCode } from '@axo/shared/types';
import {
  createContext,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { AxoFormOptions, useAxoForm } from '../../hooks/useAxoForm';
import { Step } from '../../types';
import { useDataAccessErrorFeedback } from '../DataAccessErrorFeedback';

export type AxoFormContextData<OptionsType extends Record<string, unknown>> = {
  baseUrl: string;
  legacyUrl?: string;
  axoFormData: OptionsType;
  step: number;
  steps: Step[];
  onChange: (data: OptionsType) => Promise<void>;
  marketCountry: MarketCountryCode;
  onInitiated: () => Promise<void>;
  onPreSubmit: (data: OptionsType) => Promise<void>;
  onPrevStep: () => void;
  onSubmit: (data: OptionsType, isPre?: boolean) => Promise<void>;
  StepComponent: () => ReactElement;
  reloadSyncData: () => Promise<void>;
  dataSharingStatus: DataSharingStatus;
  formRef?: RefObject<HTMLFormElement>;
  numberOfCompletedSteps: number;
};

const FormDataContext = createContext({});

type FormContextProviderProps<OptionsType extends Record<string, unknown>> = {
  /** Product to use for the analytics data and session storage key */
  product?: Product;
  children: ReactNode;
  baseUrl: string;
  legacyUrl?: string;
  steps: Step[];
  isPre?: boolean;
  mutators: Mutators<OptionsType>;
  marketCountry: MarketCountryCode;
  dataSharingStatus?: DataSharingStatus;
  formRef?: RefObject<HTMLFormElement>;
  configureSteps?: (data: OptionsType) => void;
  onStepChangeScrollTo?: AxoFormOptions<OptionsType>['onStepChangeScrollTo'];
} & Pick<AxoFormOptions<OptionsType>, 'redirectUrl' | 'onCompleted'>;

const AxoFormContextProvider = <OptionsType extends Record<string, unknown>>({
  children,
  product,
  mutators,
  baseUrl,
  legacyUrl,
  redirectUrl,
  steps,
  isPre = false,
  marketCountry,
  dataSharingStatus,
  formRef,
  onStepChangeScrollTo,
  configureSteps,
  onCompleted,
}: FormContextProviderProps<OptionsType>) => {
  const {
    axoFormData,
    initialized,
    syncData,
    step,
    onInitiated,
    setStep,
    StepComponent,
    submitStep,
    submitApplication,
    reloadSyncData,
    numberOfCompletedSteps,
  } = useAxoForm<OptionsType>({
    product,
    formRef,
    baseUrl,
    redirectUrl,
    onCompleted,
    steps,
    mutators,
    marketCountry,
    isPre,
    configureSteps,
    dataSharingStatus,
    onStepChangeScrollTo,
  });

  const { setError } = useDataAccessErrorFeedback();

  const onChange = useCallback(
    async (data: OptionsType) => {
      try {
        await syncData(data);
      } catch (error) {
        if (error instanceof Error) setError(error);

        throw error;
      }
    },
    [setError, syncData]
  );

  const onPreSubmit = useCallback(
    async (data: OptionsType) => {
      await submitStep(data, true);

      if (redirectUrl) window.location.href = redirectUrl.toString();

      onCompleted?.();
    },
    [submitStep, redirectUrl, onCompleted]
  );

  const onSubmit = useCallback(
    async (data: OptionsType) => {
      if (step < steps.length) {
        return submitStep(data);
      }

      try {
        await submitApplication(data);
      } catch (error) {
        if (error instanceof Error) setError(error);
        throw error;
      }
    },
    [step, steps.length, submitStep, submitApplication, setError]
  );

  const onPrevStep = useCallback(() => {
    setStep(step - 1);
  }, [step, setStep]);

  const formDataContextValue = useMemo(
    () => ({
      formRef,
      baseUrl,
      legacyUrl,
      axoFormData,
      step,
      steps,
      onChange,
      onInitiated,
      onPreSubmit,
      onPrevStep,
      onSubmit,
      StepComponent,
      dataSharingStatus,
      reloadSyncData,
      numberOfCompletedSteps,
      marketCountry,
    }),
    [
      formRef,
      baseUrl,
      legacyUrl,
      axoFormData,
      step,
      steps,
      onChange,
      onInitiated,
      onPreSubmit,
      onPrevStep,
      onSubmit,
      StepComponent,
      dataSharingStatus,
      reloadSyncData,
      numberOfCompletedSteps,
      marketCountry,
    ]
  );

  return initialized ? (
    <FormDataContext.Provider value={formDataContextValue}>
      {children}
    </FormDataContext.Provider>
  ) : null;
};

const useAxoFormContext = <T extends Record<string, unknown>>() => {
  const context = useContext(FormDataContext) as AxoFormContextData<T>;

  if (!context) {
    throw new Error(
      'useAxoFormContext must be used within a AxoFormContextProvider'
    );
  }

  return context;
};

export { AxoFormContextProvider, useAxoFormContext };
